/**
 * The last run as a pipeline: Scout job → rows kept → fields mapped → matched
 * → sent to the import → written, with the number at each step and the first
 * step that has a problem.
 *
 * Every number comes from a run's own counts, so the picture always adds up
 * to what the Runs tab says.
 */

import type { Run } from "@/lib/integrations/api";
import { isMapped, type Settings } from "@/lib/integrations/settings";
import { scoutRunStatus } from "@/lib/integrations/words";

export type StepState = "good" | "warning" | "danger" | "idle";

export type PipelineKey = "scout" | "kept" | "mapped" | "matched" | "sent" | "written";

export type PipelineStep = { key: PipelineKey; title: string; value: string; note: string; state: StepState };

export type PipelineInput = {
  /** The newest real run omnium made (not a try). */
  run: Pick<Run, "status" | "rowsCollected" | "counts" | "scoutRunId"> | null;
  /** Scout's newest run of the job, if Scout described it. */
  scoutNewest: { id: string; status: string; rows: number } | null;
  /** The import's fields, once loaded. */
  fields: { key: string; required: boolean; many?: boolean }[] | null;
  settings: Pick<Settings, "fields">;
  /** Whether it reads Scout runs now. Setting up, paused or off, an unread run is expected, not a problem. */
  reading?: boolean;
};

function number(value: number): string {
  return value.toLocaleString("en-GB");
}

function rows(count: number): string {
  return `${number(count)} ${count === 1 ? "row" : "rows"}`;
}

export function pipelineSteps({ run, scoutNewest, fields, settings, reading = true }: PipelineInput): PipelineStep[] {
  const counts = run?.counts ?? {};
  const get = (key: string) => Math.max(0, counts[key] ?? 0);
  const collected = Math.max(0, run?.rowsCollected ?? 0);
  const skipped = get("skipped");
  const kept = Math.max(0, collected - skipped);
  const problem = get("problem");
  const notPlaced = get("not_placed") + get("held");
  const refused = get("refused");
  const placed = Math.max(0, kept - problem - notPlaced);
  const written = get("accepted");
  const unchanged = get("unchanged");
  const byHand = get("kept_by_hand");
  const noRun = "No run yet";

  let scout: PipelineStep;
  if (scoutNewest) {
    const said = scoutRunStatus(scoutNewest.status);
    const passed = scoutNewest.status.toLowerCase() === "passed";
    const read = run !== null && run.scoutRunId === scoutNewest.id;
    const unread = passed && !read;
    scout = {
      key: "scout",
      title: "Scout job",
      value: rows(scoutNewest.rows),
      note: unread
        ? reading
          ? "Newest passed run is not read yet"
          : "Newest run passed. Not read until switched on."
        : `Newest run: ${said.word.toLowerCase()}`,
      state: said.tone === "danger" ? "danger" : unread ? (reading ? "warning" : "idle") : passed ? "good" : "idle",
    };
  } else {
    scout = run
      ? { key: "scout", title: "Scout job", value: rows(collected), note: "Sent by Scout", state: "good" }
      : { key: "scout", title: "Scout job", value: "—", note: noRun, state: "idle" };
  }

  const keptStep: PipelineStep = !run
    ? { key: "kept", title: "Rows kept", value: "—", note: noRun, state: "idle" }
    : {
        key: "kept",
        title: "Rows kept",
        value: rows(kept),
        note: skipped > 0 ? `${number(skipped)} left out by keep rules` : "Every row kept",
        state: kept === 0 && collected > 0 ? "warning" : "good",
      };

  let mapped: PipelineStep;
  if (fields) {
    const count = fields.filter((field) => isMapped(settings.fields[field.key])).length;
    const missing = fields.filter((field) => field.required && !field.many && !isMapped(settings.fields[field.key])).length;
    mapped = {
      key: "mapped",
      title: "Fields mapped",
      value: `${number(count)} of ${number(fields.length)}`,
      note:
        missing > 0
          ? `${number(missing)} needed ${missing === 1 ? "field is" : "fields are"} not mapped`
          : problem > 0
            ? `${rows(problem)} could not be read`
            : run
              ? "Every needed field has a value"
              : "Every needed field is mapped",
      state: missing > 0 ? "danger" : problem > 0 ? "warning" : "good",
    };
  } else {
    const count = Object.values(settings.fields).filter((setting) => isMapped(setting)).length;
    mapped = {
      key: "mapped",
      title: "Fields mapped",
      value: `${number(count)} mapped`,
      note: problem > 0 ? `${rows(problem)} could not be read` : "Reading the import's fields",
      state: problem > 0 ? "warning" : "idle",
    };
  }

  if (!run) {
    return [
      scout,
      keptStep,
      mapped,
      { key: "matched", title: "Matched", value: "—", note: noRun, state: "idle" },
      { key: "sent", title: "Sent to the import", value: "—", note: noRun, state: "idle" },
      { key: "written", title: "Written", value: "—", note: noRun, state: "idle" },
    ];
  }

  const others = [unchanged > 0 ? `${number(unchanged)} unchanged` : null, byHand > 0 ? `${number(byHand)} kept by hand` : null].filter(Boolean);
  const writtenStep: PipelineStep =
    run.status === "failed"
      ? { key: "written", title: "Written", value: rows(written), note: "The run failed", state: "danger" }
      : run.status === "paused_by_guard"
        ? { key: "written", title: "Written", value: "0 rows", note: "A guard paused it. Nothing was written.", state: "danger" }
        : {
            key: "written",
            title: "Written",
            value: rows(written),
            note: others.length ? others.join(" · ") : written > 0 ? "Every row was new or changed" : "Nothing to write",
            state: "good",
          };

  return [
    scout,
    keptStep,
    mapped,
    {
      key: "matched",
      title: "Matched",
      value: rows(placed),
      note: notPlaced > 0 ? `${rows(notPlaced)} not placed or held` : "Every row found its record",
      state: notPlaced > 0 ? "warning" : "good",
    },
    {
      key: "sent",
      title: "Sent to the import",
      value: rows(placed),
      note: refused > 0 ? `${rows(refused)} refused by a rule` : "No row refused",
      state: refused > 0 ? "danger" : "good",
    },
    writtenStep,
  ];
}

/** The first step with a problem, or -1. */
export function firstProblem(steps: PipelineStep[]): number {
  return steps.findIndex((step) => step.state === "danger" || step.state === "warning");
}
